import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRecoilState, useRecoilValue } from 'recoil';
import ReactPlayer from '@components/library/react-player';
import { muteState, playState, globalBgmPausedState, routeBgmMutedState, volumeState } from '@atom/setting/jukebox';
import { IJukeboxItem, Ijukebox } from '@typings/setting/jukebox.interface';
import Icon from '@components/dataDisplay/Icons/Icon';

const PlayerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NowPlayingInfo = styled.div<{ $cardColor?: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ $cardColor }) => $cardColor || 'rgba(0, 0, 0, 0.2)'};
  border-radius: 8px;
`;

const AlbumArt = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
`;

const AlbumArtPlaceholder = styled.div<{ $buttonColor?: string }>`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: ${({ $buttonColor }) => $buttonColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TrackInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const TrackTitle = styled.div<{ $textColor?: string }>`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $textColor }) => $textColor || 'inherit'};
`;

const TrackArtist = styled.div<{ $subTextColor?: string }>`
  font-size: 12px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const ControlsSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const VolumeWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const VolumePopover = styled.div<{ $bgColor?: string; $lineColor?: string }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${({ $bgColor }) => $bgColor || 'rgba(30, 30, 30, 0.95)'};
  border: 1px solid ${({ $lineColor }) => $lineColor || 'rgba(255,255,255,0.12)'};
  border-radius: 8px;
  padding: 10px 14px;
  width: 140px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 10;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
`;

const VolumeLabel = styled.div<{ $subTextColor?: string }>`
  font-size: 10px;
  opacity: 0.7;
  text-align: center;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const VolumeSlider = styled.input<{ $buttonColor?: string; $lineColor?: string }>`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  background: linear-gradient(
    to right,
    ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'} var(--vol),
    ${({ $lineColor }) => $lineColor || 'rgba(255,255,255,0.2)'} var(--vol)
  );

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'};
    cursor: pointer;
    box-shadow: 0 0 4px rgba(0,0,0,0.3);
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: none;
    background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'};
    cursor: pointer;
  }
`;

const ControlButton = styled.button<{ $size?: 'sm' | 'md' | 'lg'; $buttonColor?: string }>`
  background: ${({ $buttonColor }) => $buttonColor ? `${$buttonColor}33` : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  border-radius: 50%;
  width: ${({ $size }) => ($size === 'lg' ? '48px' : $size === 'sm' ? '32px' : '40px')};
  height: ${({ $size }) => ($size === 'lg' ? '48px' : $size === 'sm' ? '32px' : '40px')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: var(--hoverCursorURL),pointer;
  transition: all 0.2s;
  color: inherit;

  &:hover {
    background: ${({ $buttonColor }) => $buttonColor ? `${$buttonColor}66` : 'rgba(255, 255, 255, 0.2)'};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  padding: 8px 0;
  cursor: pointer;
  position: relative;
`;

const ProgressBar = styled.div<{ $lineColor?: string }>`
  width: 100%;
  height: 4px;
  background: ${({ $lineColor }) => $lineColor || 'rgba(255, 255, 255, 0.2)'};
  border-radius: 2px;
  position: relative;
`;

const Progress = styled.div<{ $progress: number; $buttonColor?: string; $isDragging: boolean }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'};
  border-radius: 2px;
  transition: ${({ $isDragging }) => ($isDragging ? 'none' : 'width 0.1s linear')};
`;

const ProgressThumb = styled.div<{ $progress: number; $buttonColor?: string; $isDragging: boolean }>`
  position: absolute;
  top: 50%;
  left: ${({ $progress }) => $progress}%;
  transform: translate(-50%, -50%);
  width: ${({ $isDragging }) => ($isDragging ? '14px' : '12px')};
  height: ${({ $isDragging }) => ($isDragging ? '14px' : '12px')};
  border-radius: 50%;
  background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'};
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
  cursor: grab;
  transition: width 0.15s, height 0.15s;
  pointer-events: none;

  &:active {
    cursor: grabbing;
  }
`;

const TimeInfo = styled.div<{ $subTextColor?: string }>`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  opacity: 0.6;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const HiddenPlayer = styled.div`
  display: none;
`;

const AudioElement = styled.audio`
  display: none;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  font-size: 13px;
  opacity: 0.7;
`;

type Props = {
  jukebox: Ijukebox;
  playlist: IJukeboxItem[];
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  onPlayerStateChange?: (progress: number, currentTime: number, duration: number) => void;
  seekRequestTime?: number;
};

function CustomPlaylistPlayer({ jukebox, playlist, currentIndex, setCurrentIndex, onPlayerStateChange, seekRequestTime }: Props) {
  const [onPlay, setPlay] = useRecoilState(playState);
  const [muted, setMuted] = useRecoilState(muteState);
  const [volume, setVolume] = useRecoilState(volumeState);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isVolumeOpen, setIsVolumeOpen] = useState(false);
  const [youtubeSeekTo, setYoutubeSeekTo] = useState<number | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const volumeWrapperRef = useRef<HTMLDivElement>(null);
  const durationRef = useRef(0);
  const sourceTypeRef = useRef<'youtube' | 'file' | null>(null);

  // 🎵 페이지 BGM에 의해 전역 BGM이 일시정지된 상태
  const isGlobalBgmPaused = useRecoilValue(globalBgmPausedState);
  // 🎵 라우트 레벨 음소거 (특정 페이지에서 완전 차단)
  const isRouteBgmMuted = useRecoilValue(routeBgmMutedState);

  // 🎵 페이지 BGM이 재생 중이거나 라우트 레벨 음소거이면 재생 안 함
  const shouldPlay = onPlay && !isGlobalBgmPaused && !isRouteBgmMuted;

  // 현재 재생 중인 트랙
  const currentTrack = playlist?.[currentIndex];

  // 트랙의 소스 타입 결정
  const getSourceType = useCallback((track: IJukeboxItem): 'youtube' | 'file' | null => {
    if (track?.file?.url) return 'file';
    if (track?.youtubeURL) return 'youtube';
    return null;
  }, []);

  const sourceType = currentTrack ? getSourceType(currentTrack) : null;

  // stale closure 방지: 최신 값을 ref에 동기화
  durationRef.current = duration;
  sourceTypeRef.current = sourceType;

  // 다음 트랙으로 이동 (자동재생 + 음소거 해제)
  const handleNext = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    setCurrentIndex((currentIndex + 1) % playlist.length);
    if (!onPlay) setPlay(true);
    if (muted) setMuted(false);
  }, [currentIndex, playlist, setCurrentIndex, onPlay, setPlay, muted, setMuted]);

  // 이전 트랙으로 이동 (자동재생 + 음소거 해제)
  const handlePrev = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    setCurrentIndex(currentIndex === 0 ? playlist.length - 1 : currentIndex - 1);
    if (!onPlay) setPlay(true);
    if (muted) setMuted(false);
  }, [currentIndex, playlist, setCurrentIndex, onPlay, setPlay, muted, setMuted]);

  // 재생/일시정지 토글 — 첫 재생 시 브라우저 자동재생 정책에 따라 음소거 해제
  const handlePlayPause = useCallback(() => {
    if (!onPlay && muted) {
      setMuted(false);
    }
    setPlay(!onPlay);
  }, [onPlay, muted, setPlay, setMuted]);

  // 볼륨 아이콘 클릭: 팝오버 토글
  const handleVolumeIconClick = useCallback(() => {
    setIsVolumeOpen((prev) => !prev);
  }, []);

  // 음소거 토글 (볼륨 슬라이더와 독립 동작)
  const handleToggleMute = useCallback(() => {
    setMuted(!muted);
  }, [muted, setMuted]);

  // 볼륨 슬라이더 변경
  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      setVolume(val);
      if (muted && val > 0) setMuted(false);
    },
    [muted, setMuted, setVolume],
  );

  // 팝오버 외부 클릭 시 닫기
  useEffect(() => {
    if (!isVolumeOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (volumeWrapperRef.current && !volumeWrapperRef.current.contains(e.target as Node)) {
        setIsVolumeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isVolumeOpen]);

  // 오디오 파일 재생 진행 상태 업데이트
  const handleAudioTimeUpdate = useCallback(() => {
    if (isDragging) return;
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100 || 0);
    }
  }, [isDragging]);

  // 오디오 파일 재생 종료 시 다음 트랙으로
  const handleAudioEnded = useCallback(() => {
    handleNext();
  }, [handleNext]);

  // YouTube 진행 상태 업데이트
  const handleYoutubeProgress = useCallback((state: { played: number; playedSeconds: number }) => {
    if (isDragging) return;
    setProgress(state.played * 100);
    setCurrentTime(state.playedSeconds);
  }, [isDragging]);

  // YouTube 재생 종료 시 다음 트랙으로
  const handleYoutubeEnded = useCallback(() => {
    handleNext();
  }, [handleNext]);

  // YouTube duration 설정
  const handleYoutubeDuration = useCallback((dur: number) => {
    setDuration(dur);
  }, []);

  // 오디오 메타데이터 로드 시 duration 설정 + 트랙 변경 후 자동재생
  const shouldPlayRef = useRef(shouldPlay);
  shouldPlayRef.current = shouldPlay;

  const handleAudioLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setDuration(audio.duration);
      if (shouldPlayRef.current) {
        audio.play().catch(() => { });
      }
    }
  }, []);

  // 특정 시간으로 seek
  const seekTo = useCallback((newTime: number, newProgress: number) => {
    if (sourceType === 'file' && audioRef.current) {
      audioRef.current.currentTime = newTime;
    } else if (sourceType === 'youtube') {
      setYoutubeSeekTo(newTime);
    }
    setCurrentTime(newTime);
    setProgress(newProgress);
  }, [sourceType]);

  // 프로그레스바에서 좌표 → progress/time 계산
  const calcSeekFromEvent = useCallback((clientX: number): { newProgress: number; newTime: number } | null => {
    const bar = progressBarRef.current;
    if (!bar || !duration) return null;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    return { newProgress: ratio * 100, newTime: ratio * duration };
  }, [duration]);

  // 드래그 시작
  const handleSeekStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    const result = calcSeekFromEvent(e.clientX);
    if (result) {
      setProgress(result.newProgress);
      setCurrentTime(result.newTime);
    }
  }, [calcSeekFromEvent]);

  // 드래그 이동 (window mousemove)
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const result = calcSeekFromEvent(e.clientX);
      if (result) {
        setProgress(result.newProgress);
        setCurrentTime(result.newTime);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      const result = calcSeekFromEvent(e.clientX);
      if (result) {
        seekTo(result.newTime, result.newProgress);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, calcSeekFromEvent, seekTo]);

  // 재생 상태를 외부에 알림 (슬라이드 패널 등에서 공유)
  useEffect(() => {
    if (!isDragging) {
      onPlayerStateChange?.(progress, currentTime, duration);
    }
  }, [progress, currentTime, duration, isDragging, onPlayerStateChange]);

  // 외부 seek 요청 처리 (PlaylistSlidePanel 등) — ref로 stale closure 방지
  useEffect(() => {
    if (seekRequestTime === undefined || seekRequestTime === null) return;
    const dur = durationRef.current;
    const src = sourceTypeRef.current;
    if (!dur) return;
    const newProgress = (seekRequestTime / dur) * 100;
    if (src === 'file' && audioRef.current) {
      audioRef.current.currentTime = seekRequestTime;
    } else if (src === 'youtube') {
      setYoutubeSeekTo(seekRequestTime);
    }
    setCurrentTime(seekRequestTime);
    setProgress(newProgress);
  }, [seekRequestTime]);

  // 오디오 재생 상태 동기화 (shouldPlay·sourceType·트랙 변경 시 반응)
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && sourceType === 'file') {
      if (shouldPlay) {
        audio.play().catch(() => { });
      } else {
        audio.pause();
      }
    }
  }, [shouldPlay, sourceType, currentIndex]);

  // 트랙 변경 시 진행 상태 리셋
  useEffect(() => {
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
  }, [currentIndex]);

  // 볼륨 설정 (volumeState 기반, jukebox.defaultVolume은 초기값으로만 사용)
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
      audio.muted = muted;
    }
  }, [volume, muted]);

  // 시간 포맷팅
  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' + s : s}`;
  };

  if (!playlist || playlist.length === 0) {
    return (
      <EmptyState>
        커스텀 플레이리스트에 음악을 추가해주세요.
      </EmptyState>
    );
  }

  // themeSet 컬러 추출
  const themeSet = jukebox?.themeSet;

  return (
    <PlayerContainer>
      {/* 현재 재생 중인 트랙 정보 */}
      <NowPlayingInfo $cardColor={themeSet?.cardColor}>
        {currentTrack?.imgURL ? (
          <AlbumArt src={currentTrack.imgURL} alt={currentTrack.title} />
        ) : (
          <AlbumArtPlaceholder $buttonColor={themeSet?.buttonColor}>
            <Icon name="Play20" />
          </AlbumArtPlaceholder>
        )}
        <TrackInfo>
          <TrackTitle $textColor={themeSet?.textColor}>{currentTrack?.title || '알 수 없는 트랙'}</TrackTitle>
          <TrackArtist $subTextColor={themeSet?.subTextColor}>{currentTrack?.artist || '알 수 없는 아티스트'}</TrackArtist>
        </TrackInfo>
      </NowPlayingInfo>

      {/* 프로그레스바 */}
      <div>
        <ProgressBarWrapper onMouseDown={handleSeekStart} ref={progressBarRef}>
          <ProgressBar $lineColor={themeSet?.lineColor}>
            <Progress $progress={progress} $buttonColor={themeSet?.buttonColor} $isDragging={isDragging} />
            <ProgressThumb $progress={progress} $buttonColor={themeSet?.buttonColor} $isDragging={isDragging} />
          </ProgressBar>
        </ProgressBarWrapper>
        <TimeInfo $subTextColor={themeSet?.subTextColor}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </TimeInfo>
      </div>

      {/* 컨트롤 버튼 */}
      <ControlsSection>
        {/* 볼륨 컨트롤 */}
        <VolumeWrapper ref={volumeWrapperRef}>
          <ControlButton
            $size="sm"
            $buttonColor={themeSet?.buttonColor}
            onClick={handleVolumeIconClick}
          >
            <Icon name={muted ? 'VolumeOff18' : 'VolumeOn18'} />
          </ControlButton>
          {isVolumeOpen && (
            <VolumePopover
              $bgColor={themeSet?.backgroundColor}
              $lineColor={themeSet?.lineColor}
            >
              <VolumeLabel $subTextColor={themeSet?.subTextColor}>
                {muted ? '음소거' : `${Math.round(volume * 100)}%`}
              </VolumeLabel>
              <VolumeSlider
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                $buttonColor={themeSet?.buttonColor}
                $lineColor={themeSet?.lineColor}
                style={{ '--vol': `${muted ? 0 : volume * 100}%` } as React.CSSProperties}
              />
              <ControlButton
                $size="sm"
                $buttonColor={themeSet?.buttonColor}
                onClick={handleToggleMute}
                style={{ alignSelf: 'center', marginTop: 2 }}
              >
                <Icon name={muted ? 'VolumeOff18' : 'VolumeOn18'} />
              </ControlButton>
            </VolumePopover>
          )}
        </VolumeWrapper>
        <ControlButton $size="sm" $buttonColor={themeSet?.buttonColor} onClick={handlePrev}>
          <Icon name="PlayPrev14" />
        </ControlButton>
        <ControlButton $size="lg" $buttonColor={themeSet?.buttonColor} onClick={handlePlayPause}>
          <Icon name={onPlay ? 'Pause20' : 'Play20'} />
        </ControlButton>
        <ControlButton $size="sm" $buttonColor={themeSet?.buttonColor} onClick={handleNext}>
          <Icon name="PlayNext14" />
        </ControlButton>
      </ControlsSection>

      {/* 숨겨진 플레이어들 */}
      {sourceType === 'file' && currentTrack?.file?.url && (
        <AudioElement
          ref={audioRef}
          src={currentTrack.file.url}
          onTimeUpdate={handleAudioTimeUpdate}
          onLoadedMetadata={handleAudioLoadedMetadata}
          onEnded={handleAudioEnded}
        />
      )}

      {sourceType === 'youtube' && currentTrack?.youtubeURL && (
        <HiddenPlayer>
          <ReactPlayer
            url={currentTrack.youtubeURL}
            playing={shouldPlay}
            seekToSeconds={youtubeSeekTo}
            muted={muted}
            volume={volume}
            width="0"
            height="0"
            onProgress={handleYoutubeProgress}
            onDuration={handleYoutubeDuration}
            onEnded={handleYoutubeEnded}
            config={{
              youtube: {
                playerVars: {
                  autoplay: 1,
                  controls: 0,
                  rel: 0,       // 관련 영상 자동재생 차단
                  fs: 0,        // 전체화면 버튼 숨김
                },
              },
            }}
          />
        </HiddenPlayer>
      )}
    </PlayerContainer>
  );
}

export default CustomPlaylistPlayer;

